import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Panchang field keys (labels come from i18n)
const PANCHANG_FIELD_KEYS = [
    { key: 'day', fixed: false },
    { key: 'englishDate', fixed: true },
    { key: 'yogam', fixed: false },
    { key: 'tithi', fixed: false },
    { key: 'nakshatram', fixed: false },
    { key: 'rahuKalam', fixed: false },
    { key: 'yamagandam', fixed: false },
    { key: 'kuligai', fixed: false },
    { key: 'abhijitMuhurtham', fixed: false },
    { key: 'evening', fixed: false },
    { key: 'favorableStars', fixed: false },
];

const getInitialPanchangData = (dateStr) => ({
    day: '',
    englishDate: dateStr,
    yogam: '',
    tithi: '',
    nakshatram: '',
    rahuKalam: '',
    yamagandam: '',
    kuligai: '',
    abhijitMuhurtham: '',
    evening: '',
    favorableStars: '',
});

const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
};

// Format date as "DD Month YYYY" (e.g., "22 December 2025")
const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-IN', options);
};

const Admin = () => {
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(formatDateForInput(new Date()));
    const [panchangData, setPanchangData] = useState(getInitialPanchangData(formatDateForInput(new Date())));
    const [updateMsg, setUpdateMsg] = useState('');
    const [dataLoading, setDataLoading] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
            if (currentUser) {
                fetchPanchangData(selectedDate);
            }
        }, (error) => {
            console.error("Admin: Auth error", error);
            setLoading(false);
            setError(error.message);
        });

        const timeout = setTimeout(() => {
            setLoading((l) => {
                if (l) {
                    console.log("Admin: Auth listener timed out");
                    return false;
                }
                return l;
            });
        }, 5000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, []);

    const fetchPanchangData = async (dateStr) => {
        setDataLoading(true);
        try {
            const docRef = doc(db, "panchang", dateStr);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setPanchangData({ ...getInitialPanchangData(dateStr), ...docSnap.data(), englishDate: dateStr });
            } else {
                setPanchangData(getInitialPanchangData(dateStr));
            }
        } catch (err) {
            console.error("Error fetching panchang:", err);
            if (err.code === 'permission-denied') {
                setError("Permission Denied: Please update your Firestore Database Rules.");
            } else {
                setError("Failed to load data. " + err.message);
            }
        } finally {
            setDataLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        fetchPanchangData(newDate);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError('Failed to login. Check credentials.');
            console.error(err);
        }
    };

    const handleLogout = () => {
        signOut(auth);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPanchangData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdate = async () => {
        try {
            await setDoc(doc(db, "panchang", selectedDate), {
                ...panchangData,
                englishDate: selectedDate,
                updatedAt: new Date().toISOString()
            });
            setUpdateMsg('Updated successfully!');
            setTimeout(() => setUpdateMsg(''), 3000);
        } catch (err) {
            console.error(err);
            setUpdateMsg('Failed to update.');
        }
    };

    // Styles
    const styles = {
        container: {
            padding: 24,
            maxWidth: 700,
            margin: '0 auto',
            fontFamily: 'system-ui, -apple-system, sans-serif',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            borderBottom: '1px solid #333',
            paddingBottom: 16,
        },
        title: {
            margin: 0,
            fontSize: 24,
            color: 'var(--gold, #d4af37)',
        },
        datePickerSection: {
            marginBottom: 24,
            padding: 16,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 8,
        },
        dateLabel: {
            display: 'block',
            marginBottom: 8,
            fontWeight: 600,
            color: 'var(--gold, #d4af37)',
        },
        dateInput: {
            padding: '10px 14px',
            fontSize: 16,
            borderRadius: 6,
            border: '1px solid #444',
            background: '#1a1a1a',
            color: '#fff',
            cursor: 'pointer',
            minWidth: 200,
        },
        dateDisplayWrapper: {
            position: 'relative',
            display: 'inline-block',
        },
        dateDisplay: {
            padding: '12px 16px',
            fontSize: 16,
            borderRadius: 8,
            border: '1px solid #444',
            background: '#1a1a1a',
            color: 'var(--gold, #d4af37)',
            cursor: 'pointer',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
        },
        calendarIcon: {
            fontSize: 18,
        },
        hiddenDateInput: {
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            cursor: 'pointer',
        },
        formGrid: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
        },
        fieldRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 8,
            border: '1px solid #333',
        },
        label: {
            minWidth: 160,
            fontWeight: 600,
            color: '#aaa',
            fontSize: 14,
        },
        labelColon: {
            marginRight: 8,
            color: '#666',
        },
        input: {
            flex: 1,
            padding: '10px 12px',
            fontSize: 14,
            borderRadius: 6,
            border: '1px solid #444',
            background: '#1a1a1a',
            color: '#fff',
            outline: 'none',
            transition: 'border-color 0.2s',
        },
        fixedValue: {
            flex: 1,
            padding: '10px 12px',
            fontSize: 14,
            color: 'var(--gold, #d4af37)',
            fontWeight: 500,
        },
        updateBtn: {
            marginTop: 24,
            padding: '14px 20px',
            width: '100%',
            background: 'var(--gold, #d4af37)',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer',
            color: '#000',
            transition: 'opacity 0.2s',
        },
        message: {
            padding: 12,
            marginBottom: 16,
            borderRadius: 6,
            textAlign: 'center',
        },
        successMsg: {
            background: '#1a3d1a',
            color: '#4ade80',
        },
        errorMsg: {
            background: '#3d1a1a',
            color: '#f87171',
        },
        logoutBtn: {
            padding: '8px 16px',
            background: 'transparent',
            border: '1px solid #666',
            borderRadius: 6,
            color: '#aaa',
            cursor: 'pointer',
            fontSize: 14,
        },
        langSwitcher: {
            display: 'flex',
            gap: 8,
            alignItems: 'center',
        },
        langBtn: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            padding: '4px 8px',
            borderRadius: 4,
        },
        loginContainer: {
            padding: 24,
            maxWidth: 400,
            margin: '80px auto',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 12,
            border: '1px solid #333',
        },
        loginTitle: {
            textAlign: 'center',
            marginBottom: 24,
            color: 'var(--gold, #d4af37)',
        },
        loginForm: {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        },
        loginInput: {
            padding: '12px 14px',
            fontSize: 16,
            borderRadius: 6,
            border: '1px solid #444',
            background: '#1a1a1a',
            color: '#fff',
        },
        loginBtn: {
            padding: '12px',
            background: 'var(--gold, #d4af37)',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
            color: '#000',
            marginTop: 8,
        },
        loadingOverlay: {
            textAlign: 'center',
            padding: 40,
            color: '#888',
        }
    };

    if (loading) return <div style={styles.loadingOverlay}>Loading...</div>;

    if (!user) {
        return (
            <div style={styles.loginContainer}>
                <h2 style={styles.loginTitle}>Admin Login</h2>
                {error && <p style={{ color: '#f87171', textAlign: 'center' }}>{error}</p>}
                <form onSubmit={handleLogin} style={styles.loginForm}>
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={styles.loginInput}
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={styles.loginInput}
                    />
                    <button type="submit" style={styles.loginBtn}>Login</button>
                </form>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>{t('admin.title')}</h2>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    {/* Language Switcher */}
                    <div style={styles.langSwitcher}>
                        <button
                            onClick={() => i18n.changeLanguage('en')}
                            style={{
                                ...styles.langBtn,
                                fontWeight: i18n.language === 'en' ? 'bold' : 'normal',
                                color: i18n.language === 'en' ? 'var(--gold, #d4af37)' : '#aaa',
                            }}
                        >
                            English
                        </button>
                        <span style={{ color: '#666' }}>|</span>
                        <button
                            onClick={() => i18n.changeLanguage('ta')}
                            style={{
                                ...styles.langBtn,
                                fontWeight: i18n.language === 'ta' ? 'bold' : 'normal',
                                color: i18n.language === 'ta' ? 'var(--gold, #d4af37)' : '#aaa',
                            }}
                        >
                            தமிழ்
                        </button>
                    </div>
                    <button onClick={handleLogout} style={styles.logoutBtn}>{t('admin.logout')}</button>
                </div>
            </div>

            {updateMsg && (
                <div style={{ ...styles.message, ...styles.successMsg }}>{updateMsg}</div>
            )}
            {error && (
                <div style={{ ...styles.message, ...styles.errorMsg }}>{error}</div>
            )}

            {/* Date Picker Section */}
            <div style={styles.datePickerSection}>
                <label style={styles.dateLabel}>{t('admin.selectDate')}:</label>
                <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    style={styles.dateInput}
                />
            </div>

            {dataLoading ? (
                <div style={styles.loadingOverlay}>Loading data...</div>
            ) : (
                <>
                    {/* Panchang Form */}
                    <div style={styles.formGrid}>
                        {PANCHANG_FIELD_KEYS.map((field) => (
                            <div key={field.key} style={styles.fieldRow}>
                                <span style={styles.label}>
                                    {t(`hero.panchang.${field.key}`)}<span style={styles.labelColon}>:</span>
                                </span>
                                {field.fixed ? (
                                    <span style={styles.fixedValue}>{formatDateForDisplay(selectedDate)}</span>
                                ) : (
                                    <input
                                        type="text"
                                        name={field.key}
                                        value={panchangData[field.key] || ''}
                                        onChange={handleChange}
                                        placeholder={t(`hero.panchang.${field.key}`)}
                                        style={styles.input}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleUpdate}
                        style={styles.updateBtn}
                        onMouseOver={(e) => e.target.style.opacity = 0.9}
                        onMouseOut={(e) => e.target.style.opacity = 1}
                    >
                        {t('admin.savePanchang')}
                    </button>
                </>
            )
            }
        </div >
    );
};

export default Admin;
